import React from "react";
import { assessmentData, CORE_INDICATOR_CODES } from "../data/assessmentData";
import "./score.css";

const RATING_CLASS = {
  "Level A": "rating-A",
  "Level B": "rating-B",
  "Level B*": "rating-Bstar",
  "Level C": "rating-C",
  "Level D": "rating-D",
};

export default function ScoreSummary({
  computed,
  scores,
  canViewResults = false,
}) {
  if (!canViewResults) {
    return (
      <div
        style={{
          padding: "1rem",
          border: "1px dashed var(--border)",
          borderRadius: "var(--radius)",
          background: "var(--surface-2)",
          color: "var(--text-2)",
          fontSize: "0.85rem",
          lineHeight: 1.5,
        }}
      ></div>
    );
  }

  const {
    p1,
    p2,
    p3,
    grandTotal,
    rating,
    gatekeeperPassed,
    completionPct,
    scoredCount,
    totalCount,
  } = computed;

  const pillars = [
    {
      label: "Training",
      emoji: "📚",
      data: p1,
      max: 450,
      color: "var(--navy)",
    },
    {
      label: "Research",
      emoji: "🔬",
      data: p2,
      max: 300,
      color: "var(--green)",
    },
    {
      label: "Community",
      emoji: "🤝",
      data: p3,
      max: 250,
      color: "var(--amber)",
    },
  ];

  // Core indicator status
  const coreStatus = [...CORE_INDICATOR_CODES].map((code) => {
    const s = scores[code] || 0;
    const ind = assessmentData.pillars
      .flatMap((p) => p.domains.flatMap((d) => d.indicators))
      .find((i) => i.code === code);
    return { code, name: ind?.name || code, score: s, ok: s >= 3 };
  });
  const coreGaps = coreStatus.filter((c) => !c.ok);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {/* Completion */}
      <div className="completion-banner">
        <span className="completion-pct">{completionPct}%</span>
        <div className="completion-bar-wrap">
          <div
            style={{
              fontSize: "0.72rem",
              color: "var(--text-3)",
              marginBottom: "0.25rem",
            }}
          >
            {scoredCount} of {totalCount} indicators scored
          </div>
          <div className="completion-bar">
            <div
              className="completion-bar-fill"
              style={{ width: `${completionPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Grand total */}
      <div
        className="grand-total-card"
        style={{ padding: "1.25rem", borderRadius: "var(--radius)" }}
      >
        <div className="grand-total-label">Grand Total</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: "0.3rem" }}>
          <span className="grand-total-score" style={{ fontSize: "2.5rem" }}>
            {grandTotal.toFixed(0)}
          </span>
          <span className="grand-total-max">/ 1,000</span>
        </div>
        <div className="grand-total-bar">
          <div
            className="grand-total-bar-fill"
            style={{ width: `${Math.min((grandTotal / 1000) * 100, 100)}%` }}
          />
        </div>
        <span
          className={`rating-badge rating-${rating.replace(/[^A-Z]/g, "").substring(0, 2) === "LV" ? "D" : rating.replace("Level ", "").replace("*", "star")}`}
          style={{ fontSize: "0.82rem", padding: "0.3rem 0.9rem" }}
        >
          {rating}
        </span>
      </div>

      {/* Pillar breakdown */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {pillars.map(({ label, emoji, data, max, color }) => (
          <div
            key={label}
            style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.78rem",
              }}
            >
              <span>
                {emoji} {label}
              </span>
              <span style={{ fontWeight: 700, color }}>
                {(data?.weighted || 0).toFixed(0)} / {max}
              </span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${Math.min(((data?.weighted || 0) / max) * 100, 100)}%`,
                  background: color,
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Gatekeeper */}
      <div
        className={`gatekeeper-status ${gatekeeperPassed ? "gatekeeper-pass" : "gatekeeper-fail"}`}
      >
        <span>{gatekeeperPassed ? "✅" : "❌"}</span>
        <span style={{ fontSize: "0.78rem" }}>
          {gatekeeperPassed
            ? "Gatekeeper OK"
            : `${coreGaps.length} CORE gap${coreGaps.length > 1 ? "s" : ""} — Level A blocked`}
        </span>
      </div>

      {/* Core gaps list */}
      {coreGaps.length > 0 && (
        <div style={{ fontSize: "0.75rem", color: "var(--red)" }}>
          <div style={{ fontWeight: 700, marginBottom: "0.25rem" }}>
            CORE indicators needing attention:
          </div>
          {coreGaps.slice(0, 6).map((c) => (
            <div
              key={c.code}
              style={{
                padding: "0.2rem 0",
                borderBottom: "1px solid var(--border)",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span style={{ color: "var(--text-2)" }}>{c.code}</span>
              <span
                style={{
                  color: c.score === 0 ? "var(--text-3)" : "var(--red)",
                  fontWeight: 600,
                }}
              >
                {c.score === 0 ? "—" : c.score}
              </span>
            </div>
          ))}
          {coreGaps.length > 6 && (
            <div style={{ marginTop: "0.25rem", color: "var(--text-3)" }}>
              +{coreGaps.length - 6} more…
            </div>
          )}
        </div>
      )}
    </div>
  );
}
